import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { currentSession } from '@/api/tokens'
import { rootEntryForSubject } from './rootEntry'
import { loadPhase2Features, type Phase2Feature } from '@/api/phase2Features'

/** 卖家/运营/内嵌货代路由。公开页显式声明 blank，其余入口统一由下方登录与主体守卫检查。 */
const routes: RouteRecordRaw[] = [
  { path: '/collector', name: 'browser-collector', component: () => import('@/views/BrowserCollectorView.vue'), meta: { layout: 'blank', title: '浏览器采集节点' } },
  {
    path: '/',
    name: 'root-entry',
    redirect: () => rootEntryForSubject(currentSession()?.subjectType),
  },
  {
    path: '/home',
    name: 'landing',
    component: () => import('@/views/LandingView.vue'),
    meta: { layout: 'blank', title: '跨境经营全流程' },
  },
  {
    path: '/ops/profit-disputes',
    name: 'ops-profit-disputes',
    component: () => import('@/views/OpsProfitDisputeView.vue'),
    meta: { title: '利润异议', subject: 'OPERATOR' },
  },
  {
    path: '/ops/returns',
    name: 'ops-returns',
    component: () => import('@/views/OpsReturnCasesView.vue'),
    meta: { title: '取消退货案件', subject: 'OPERATOR' },
  },
  {
    path: '/ops/extension-releases',
    name: 'ops-extension-releases',
    component: () => import('@/views/OpsExtensionReleaseView.vue'),
    meta: { title: '插件发布', subject: 'OPERATOR' },
  },
  {
    path: '/ops/market-collector',
    name: 'ops-market-collector',
    component: () => import('@/views/OpsMarketCollectorView.vue'),
    meta: { title: '市场采集', subject: 'OPERATOR' },
  },
  {
    /**
     * 🔴 平台运营 · 佣金费率配置 —— 市场分析佣金费率表（类目×价格档）的维护入口。
     *
     * <h2>⚠️ 与费率配置台同型：安全靠后端</h2>
     * 后端 {@code /ops/v1/market-analysis/commission-rates/**}（并行开发中，形状以
     * {@code api/opsCommissionRates.ts} 为准）判 OPERATOR 主体。这里的
     * {@code meta.subject='OPERATOR'} 只是「别让人走进一个必然 403 的界面」，⛔ 不是权限。
     *
     * <h2>与「费率配置」(/ops/fee-profiles) 是两件事</h2>
     * fee-profiles 是我方定价模块的费率版本行；这屏是 Ozon 平台佣金费率表
     * （类目×价格档，rFBS/FBP 各三档，契约 2026-09-26 冻结），供市场分析 View 下发给插件。
     */
    path: '/ops/commission-rates',
    name: 'ops-commission-rates',
    component: () => import('@/views/OpsCommissionRatesView.vue'),
    meta: { title: '佣金费率', subject: 'OPERATOR' },
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: { layout: 'blank', title: '登录' },
  },
  {
    path: '/change-password',
    name: 'change-password',
    component: () => import('@/views/SellerSettingsView.vue'),
    meta: { layout: 'blank', title: '设置登录密码' },
  },
  {
    path: '/extension-session',
    name: 'extension-session',
    component: () => import('@/views/ExtensionSessionView.vue'),
    meta: { layout: 'blank', title: '同步插件登录状态' },
  },
  {
    path: '/register',
    name: 'register',
    component: () => import('@/views/RegisterView.vue'),
    meta: { layout: 'blank', title: '账号申请' },
  },
  {
    /**
     * 🔴 今日工作台。
     * 后端只有 `GET /api/v1/workbench/todos` 一个端点，且**不接受任何入参**
     * ⇒ ⛔ 这里不要加 `?page=` / `?sort=` 之类的路由参数，
     *    那会诱导有人去发一个后端根本不认的请求。
     */
    path: '/workbench',
    name: 'workbench',
    component: () => import('@/views/WorkbenchView.vue'),
    meta: { title: '今日工作台' },
  },
  {
    path: '/reports/sales',
    name: 'sales-screen',
    component: () => import('@/views/SalesScreenView.vue'),
    meta: { title: '销售大屏', subject: 'TENANT', phase2Feature: 'SALES_SCREEN' },
  },
  {
    path: '/intelligence',
    name: 'ozon-intelligence',
    component: () => import('@/views/OzonIntelligenceView.vue'),
    meta: { title: 'Ozon 情报中心' },
  },
  {
    path: '/selection/analytics',
    name: 'selection-analytics',
    component: () => import('@/views/SelectionAnalyticsView.vue'),
    meta: { title: '选品数据', phase2Feature: 'SELECTION_ANALYTICS' },
  },
  {
    path: '/assistant',
    name: 'native-assistant',
    component: () => import('@/views/NativeAssistantView.vue'),
    meta: { title: '经营助手', subject: 'TENANT' },
  },
  {
    path: '/sourcing/1688',
    name: 'alibaba-1688-sourcing',
    component: () => import('@/views/Alibaba1688SourcingView.vue'),
    meta: { title: '1688 选品铺货', phase2Feature: 'ALIBABA1688' },
  },
  {
    /**
     * B9：1688 账号授权（诚实降级）—— 授权状态卡 + 流程预留。
     * 🔴 未授权是这一页的正常态：入口按钮禁用 + 说明，不造授权链接。
     * 与选品铺货同受 ALIBABA1688 门控（同一功能族）。
     */
    path: '/sourcing/1688/auth',
    name: 'alibaba-1688-auth',
    component: () => import('@/views/Alibaba1688AuthView.vue'),
    meta: { title: '1688 账号授权', phase2Feature: 'ALIBABA1688' },
  },
  {
    /**
     * 🔴🔴 促销三页（B8，竞品 2026-09-11 活体走查对标）—— 后端 `PromotionController` 族
     * `/api/v1/promotion-center/**`（activities 快照缓存列表 / activities/refresh 按需拉取 /
     * activities/{id}/participations 分页 / addable-products 店铺在线商品检索 /
     * tasks 批量变更+逐项结果+回执）。
     * ⚠️ 与 `/api/v1/promotions/**`（PromotionGuardPanel 的旧预演族、auto-removal）无关，别混。
     *
     * <h2>三页一条流程，但路由各自可直达（URL 是事实来源）</h2>
     * `/promotions` 活动列表（11 列）→「查看促销中商品」`/promotions/products?shopId=&activityId=`
     * （9 列 + 批量移除/批量提交修改）→「添加商品」`/promotions/add?shopId=&activityId=`（搜索+勾选+设价）。
     * 🔴 `shopId` 是后端每条读接口的必填参数，和 `activityId` 一样放在 query（刷新/分享可复现）。
     * tab 折叠规则：三页折回 `/promotions` 一个 tab（同 /selection 范式，见 tabs.ts）。
     *
     * <h2>🔴 Pilot 关 ≠ 页面关</h2>
     * pilot-status 只关**写**（tasks）：`writePilotEnabled=false` 时任务仍会被受理，
     * 但停在 PAUSED+EXTERNAL_REQUIRED（等待外部写入）；活动/商品数据照读——
     * 菜单/路由不做 Pilot 门控（看不见页面解决不了任何问题）。
     */
    path: '/promotions',
    name: 'promotion-activities',
    component: () => import('@/views/PromotionActivityListView.vue'),
    meta: { title: '促销活动' },
  },
  {
    /** 促销中商品 —— shopId + activityId 必带（都从活动列表带入；缺参时页面给指引、不发请求） */
    path: '/promotions/products',
    name: 'promotion-products',
    component: () => import('@/views/PromotionProductsView.vue'),
    meta: { title: '促销中商品' },
  },
  {
    /** 添加商品到促销 —— shopId + activityId 必带；店铺在线商品检索 + 勾选 + 逐行设价 → ADD Command */
    path: '/promotions/add',
    name: 'promotion-add-products',
    component: () => import('@/views/PromotionAddProductsView.vue'),
    meta: { title: '添加商品到促销' },
  },
  {
    path: '/phase2/auto-promotion-removal',
    name: 'auto-promotion-removal',
    component: () => import('@/views/PromotionAutoRemovalView.vue'),
    meta: { title: '促销自动移出', phase2Feature: 'AUTO_PROMOTION_REMOVAL' },
  },
  {
    path: '/phase2/advanced-messaging',
    name: 'advanced-messaging',
    component: () => import('@/views/AdvancedMessagingView.vue'),
    meta: { title: '高级消息策略', phase2Feature: 'ADVANCED_MESSAGING' },
  },
  {
    /**
     * 订单列表 —— `GET /api/v1/orders`。
     *
     * `?todo=<category>` 是后端 `WorkbenchTodoVO.GroupVO.category` 定的筛选值形状
     * （与工作台待办分类同一套字面量），工作台「查看全部」带过来的 query 在这一页
     * 直接命中对应待办页签。还支持 `?orderNo=` / `?shopId=` / `?page=`。
     */
    path: '/orders',
    name: 'orders',
    component: () => import('@/views/OrderListView.vue'),
    meta: { title: '订单' },
  },
  {
    /** 订单详情 —— `GET /api/v1/orders/{id}` */
    path: '/orders/:orderId(\\d+)',
    name: 'order-detail',
    component: () => import('@/views/OrderDetailView.vue'),
    meta: { title: '订单详情' },
    props: true,
  },
  {
    path: '/returns',
    name: 'seller-returns',
    component: () => import('@/views/SellerReturnsView.vue'),
    meta: { title: '退货案件', subject: 'TENANT' },
  },
  {

    /**
     * B10：订单数据大屏 —— `GET /api/v1/orders/stats/dashboard?days=30`（只读）。
     * 挂在「订单」组；与 `/reports/sales`（销售大屏，SALES_SCREEN pilot）是两个页面：
     * 这一页是订单域统计契约，那一页是 metrics 口径的报表。
     */
    path: '/datascreen',
    name: 'datascreen',
    component: () => import('@/views/DataScreenView.vue'),
    meta: { title: '数据大屏' },
  },
  {
    /**
     * 🔴 订单消息 · 模板列表（B7 竞品「消息模板」对标）—— `GET /api/v1/orders/message-templates`。
     *
     * 五列：消息类型 / 消息内容（俄+中+变量）/ 自动发送的店铺 / 开启自动发送 / 操作。
     * 真实契约（真机实测）锁定在 `api/orderMessages.contract.test.ts`：改内容/启停走
     * `POST /api/v1/orders/message-templates/{id}`（带 expectedVersion 乐观锁），
     * 按店绑定是模板里嵌的 `bindings[]` + 逐店 POST/DELETE。
     * ⚠️ 后端没有「新建模板」端点（三类模板由 seed 提供）⇒ 页面不提供新建入口；
     * 门槛（Premium 订阅 / 聊天权限店铺 / 自动同步）没有独立可用性端点：
     * 模板列表拉不到 = 功能未开放，页面按后端原句如实显示并禁用动作（⛔ 不造假）。
     */
    path: '/messages',
    name: 'order-messages',
    component: () => import('@/views/MessagesView.vue'),
    meta: { title: '消息' },
  },
  {
    /**
     * 🔴 订单消息 · 发送记录（B7 竞品独立「发送记录」页）——
     * `GET /api/v1/orders/message-send-records?shopId=&page=&size=`。
     * 🔴 后端**不支持按状态查询**：状态筛选是前端过滤已加载页，页面如实标注。
     * 列：触发 / 店铺 / 订单 / 消息类型 / 状态（SENT 才是发出；INTENDED = 已登记未发送）。
     * 独立页（竞品同位），与模板列表平级挂菜单「订单」组，各占一个 tab。
     */
    path: '/messages/records',
    name: 'order-message-records',
    component: () => import('@/views/MessageSendRecordsView.vue'),
    meta: { title: '发送记录' },
  },
  {
    /**
     * 商品 / SKU 列表 —— `GET /api/v1/product/skus`。
     *
     * 🔴 与「采集箱」（/collection）不同：那一个是插件采下来的候选商品，
     * 这一个是<b>已建档的 SKU</b>（按 skuCode 精确搜或列全部）。
     * 后端不下发成本/采购价/状态，详见 ProductListView.vue 的注释。
     */
    path: '/products',
    name: 'products',
    component: () => import('@/views/ProductListView.vue'),
    meta: { title: '在线商品' },
  },
  {
    /**
     * D2 · 在线商品编辑页（PLAN-PARITY-20260922 定稿）：四区（主要信息/属性/变体/图片）+ AI 按钮族。
     * 侧栏不挂菜单——只从在线商品行操作「编辑」进；有采集项时页内给「编辑草稿」深链。
     * 深路径不注册 TOP_LEVEL_PATHS（非顶层页，tabs 折叠不收录）。
     */
    path: '/products/:listingId/edit',
    name: 'productEdit',
    component: () => import('@/views/ProductEditView.vue'),
    meta: { title: '编辑商品' },
  },
  {
    /**
     * 上架记录（竞品「商品→上架记录」同位入口，f-070）。
     * 本批占位：ListingRecordsView 解释台账在哪、真页（跨任务列表+明细抽屉）第三版接入。
     * ⚠️ tabs 折叠规则：TOP_LEVEL_PATHS 若不含本路径需要登记 —— 见 tabs.ts 的白名单。
     */
    path: '/listing/records',
    name: 'listingRecords',
    component: () => import('@/views/ListingRecordsView.vue'),
    meta: { title: '上架记录' },
  },
  {
    /**
     * 🔴🔴 商品 / 采集箱 —— 同样是**后端零 Controller**。
     *
     * `collection_item` / `product` / `product_sku` / `shop_listing` 表都建好了
     * （sql/V2），但 `erp-api` 下一个读写它们的 Controller 都没有。
     * ⇒ 这一页只说清两件事：为什么现在没有，以及**插件采到的东西现在存在哪**
     * （答案是「只在这台浏览器的 `chrome.storage.local` 里」）。
     */
    path: '/collection',
    name: 'collection',
    component: () => import('@/views/CollectionInboxView.vue'),
    meta: { title: '商品 · 采集箱' },
  },
  {
    /**
     * 🔴 选品 · 新建任务（3 步向导，二期 T10）—— `POST /api/v1/selection/tasks`。
     *
     * 第 1 步来源（粘贴 Ozon 链接或手工候选 SKU；过滤条件折叠进「高级」）→
     * 第 2 步规则（模板预填，模板来自 `GET /api/v1/selection/templates`）→
     * 第 3 步预演（dryRun 默认 true，建任务一步返回预览）。
     * ⚠️ 链接解析是 T9 的活：后端只认 candidateSkuIds，向导里链接只作记录。
     * 🔴 幂等：每次打开向导生成一个 clientOperationId，重传返回同一任务。
     */
    path: '/selection/new',
    name: 'selection-wizard',
    component: () => import('@/views/SelectionTaskWizardView.vue'),
    meta: { title: '选品 · 新建任务' },
  },
  {
    /**
     * 🔴 选品 · 任务列表（状态/进度/新增 X/淘汰 Y/待审 Z 摘要）。
     *
     * 数据来源是后端 `GET /api/v1/selection/tasks`（分页查当前租户的任务）。
     * localStorage 记忆用于「重新预演」需要原幂等键的场景（PENDING/FAILED 任务）。
     */
    path: '/selection/tasks',
    name: 'selection-tasks',
    component: () => import('@/views/SelectionTaskListView.vue'),
    meta: { title: '选品' },
  },
  {
    /**
     * 选品 · 预演结果 —— `GET /api/v1/selection/tasks/{id}`（进度 + 预览回放）。
     * 三组清单（能做/边缘/必亏，只按后端 verdict 分组）+ 保本价分布直方图；
     * 「确认执行」调 `POST /api/v1/selection/tasks/{id}/execute`（唯一落库阶段，幂等）。
     */
    path: '/selection/tasks/:taskId(\\d+)/preview',
    name: 'selection-task-preview',
    component: () => import('@/views/SelectionTaskPreviewView.vue'),
    meta: { title: '选品 · 预演结果' },
    props: true,
  },
  {
    path: '/shops',
    name: 'shops',
    component: () => import('@/views/ShopListView.vue'),
    meta: { title: '我的店铺' },
  },
  {
    path: '/shops/:shopId(\\d+)',
    name: 'shop-detail',
    component: () => import('@/views/ShopDetailView.vue'),
    meta: { title: '店铺详情' },
    props: true,
  },
  {
    /**
     * 店铺分组（竞品 batch-02 对标，后端 sql/V1052 + `/api/v1/shop-groups`）。
     * 多对多：一个分组多个店铺、同一店铺多个分组；每成员可带仓库/库存覆盖。
     */
    path: '/shop-groups',
    name: 'shop-groups',
    component: () => import('@/views/ShopGroupListView.vue'),
    meta: { title: '店铺分组' },
  },
  {
    path: '/watermarks',
    name: 'watermarks',
    component: () => import('@/views/WatermarkSettingsView.vue'),
    meta: { title: '水印管理', subject: 'TENANT' },
  },
  {
    /**
     * 钱包（余额 · 充值 · 流水）。
     * 🔴 一期只有「线下转账 + 人工入账」，⛔ 页面上不放微信/支付宝按钮 ——
     *    那是被 T6 §4.1 砍到 M1.5 的东西，摆一个灰按钮会被当成"马上就有"。
     */
    path: '/wallet',
    name: 'wallet',
    component: () => import('@/views/WalletView.vue'),
    meta: { title: '我的钱包' },
  },
  {
    path: '/billing',
    name: 'billing',
    component: () => import('@/views/BillingView.vue'),
    meta: { title: '套餐与用量', subject: 'TENANT' },
  },
  {
    /**
     * 费率 / 保本价 —— `GET /api/v1/pricing/fees/resolve`。
     * 🔴 后端没有「按店铺返回费率构成」的接口：每一项费率都要单独 resolve 一次。
     * 选店铺是为了缩小解析范围（六层继承里的「店铺」这一层）。
     */
    path: '/pricing',
    name: 'pricing',
    component: () => import('@/views/PricingView.vue'),
    meta: { title: '费率 / 保本价' },
  },
  {
    /**
     * 🔴 定价工具 / 利润计算器（FE-5，竞品 confirmedWorkflows.pricingAndProfit 双页签对标）。
     *
     * 与上面 `/pricing`（保本价试算，后端接口驱动）是两个东西：这一页是<b>纯前端计算器</b>，
     * ⛔ 不调任何后端 —— 费率/汇率/成本快照存 localStorage（前缀 saaserp.pricing.*），
     * 页面明示"本地配置，不上传"。公式全部在 seller/src/utils/pricing.ts（纯函数，插件侧复用）。
     * 工具组菜单两个入口（定价工具 / 利润计算器）同页双页签，`?tab=profit` 直达利润计算器。
     */
    path: '/tools/pricing',
    name: 'tools-pricing',
    component: () => import('@/views/ToolsPricingView.vue'),
    meta: { title: '定价工具 · 利润计算器' },
  },
  {
    /**
     * 物流在途 —— `GET /api/v1/fulfillment/orders/{id}`。
     * 🔴 输入的是履约单 id（数据库主键），不是平台单号。
     * 「不存在」与「不是你的」给出完全相同的 403（越权矩阵）。
     */
    path: '/logistics',
    name: 'logistics',
    component: () => import('@/views/LogisticsView.vue'),
    meta: { title: '物流在途' },
  },
  {
    /**
     * 设置 —— 当前只有「修改密码」一项。
     * 🔴 改密调的是已有的 `POST /auth/v1/password`（三端共用，不在前缀闸门下）。
     */
    path: '/settings',
    name: 'settings',
    component: () => import('@/views/SellerSettingsView.vue'),
    meta: { title: '设置' },
  },
  {
    /**
     * 帮助中心 —— 静态内容页（doc/55 D14）。
     * 🔴 纯静态 FAQ + 操作指引，不调任何后端。挂在侧边栏「配置」组的末尾。
     */
    path: '/help',
    name: 'help',
    component: () => import('@/views/HelpView.vue'),
    meta: { title: '帮助中心' },
  },
  {
    /**
     * 🔴 平台运营 · 充值确认台。
     *
     * <h2>⚠️ 为什么它和卖家端在**同一个应用**里</h2>
     * 两者都是**桌面管理后台**，交互形态一致；而货代端是 PWA（仓库设备、离线、单手），
     * 那才是必须分出去的那一个。
     * <p>安全上不构成问题：后端 `SubjectTypeGuardFilter` 把 `/ops/v1/**` 限定为
     * OPERATOR 令牌 —— 卖家即使加载到这段 JS，每一个请求也都会 403。
     * 🔴 下面的 `meta.subject` 只是**不让人走进一个必然全是错误的界面**，
     * ⛔ 它不是权限，真正的权限在后端。
     * <p>⇒ 运营端功能长起来之后应当拆成第三个应用。现在它只有一屏，不值得多一套脚手架。
     */
    path: '/ops/recharges',
    name: 'ops-recharges',
    component: () => import('@/views/OpsRechargeView.vue'),
    meta: { title: '充值确认台', subject: 'OPERATOR' },
  },
  {
    /**
     * 🔴 平台运营 · 统计报表看板。
     *
     * <h2>⚠️ 与充值确认台同型：桌面管理后台的一屏，安全靠后端</h2>
     * 后端 `SubjectTypeGuardFilter` 把 `/ops/v1/**` 限定为 OPERATOR 令牌，
     * `OpsReportController` 自己再判一次主体类型 + `ops:account:view` 权限点。
     * 这里的 `meta.subject='OPERATOR'` 只是「别让人走进一个必然 403 的界面」，⛔ 不是权限。
     *
     * <h2>🔴 数据是「每天凌晨算好的」，不是实时的</h2>
     * 聚合作业（`StatisticsAggregationService`）在 worker 角色里每天 03:00 UTC 跑昨天。
     * ⇒ 这屏读的是预算好的两张统计表，**不是**现场聚合。当天数据要等明天才出现。
     */
    path: '/ops/reports',
    name: 'ops-reports',
    component: () => import('@/views/OpsReportsView.vue'),
    meta: { title: '统计报表', subject: 'OPERATOR' },
  },
  {
    /**
     * 🔴 平台运营 · 货代结算付款管理台（O1）。
     *
     * <h2>⚠️ 与上面两屏同型：桌面管理后台的一屏，安全靠后端</h2>
     * 后端 `OpsSettlementController`（`/ops/v1/forwarder/settlements`）判 OPERATOR 主体 +
     * `ops:account:view` 权限点。这里的 `meta.subject='OPERATOR'` 只是
     * 「别让人走进一个必然 403 的界面」，⛔ 不是权限。
     *
     * <h2>🔴🔴 这屏「登记付款」是碰钱方向的操作，但不发起资金流出</h2>
     * doc/46 §3.3 定死：系统出结算单 → 财务在<b>网银手工付</b> → 回系统<b>登记</b>
     * （记 paid_amount + 我方付款流水号，状态推进 CONFIRMED → PAID）。
     * 系统不发起任何付款 —— 那是 M2「自动打款」才接上的第二条路径。
     *
     * <h2>🔴 端点在 {@code /ops/v1/} 而不是 {@code /fwd/v1/}</h2>
     * 登记付款是<b>平台运营（财务）</b>的动作，与货代自己确认结算单（`/fwd/v1/...`）
     * 是<b>两个不同的人做的</b>。`SubjectTypeGuardFilter` 把 `/fwd/v1/` 限定为 FORWARDER 主体，
     * 运营令牌打不进那个前缀 ⇒ 这一族在 `/ops/v1/` 下。
     */
    path: '/ops/forwarder-settlements',
    name: 'ops-forwarder-settlements',
    component: () => import('@/views/OpsForwarderSettlementView.vue'),
    meta: { title: '货代结算', subject: 'OPERATOR' },
  },
  {
    /**
     * 🔴 平台运营 · 货代资质审核台（O2）。
     *
     * <h2>⚠️ 与上面几屏同型：桌面管理后台的一屏，安全靠后端</h2>
     * 后端 {@code OpsForwarderController}（{@code /ops/v1/forwarders}）判 OPERATOR 主体 +
     * {@code ops:account:view} 权限点。这里的 {@code meta.subject='OPERATOR'} 只是
     * 「别让人走进一个必然 403 的界面」，⛔ 不是权限。
     *
     * <h2>🔴🔴 审核是「签约闸」</h2>
     * {@code forwarder.status} 起步 {@code PENDING}：注册上来不等于能接单，
     * 必须平台审核签约后才能上架（{@code LISTED}）被卖家绑定。⇒ 这屏挡的是「没签约的货代开始接单」。
     */
    path: '/ops/forwarders',
    name: 'ops-forwarders',
    component: () => import('@/views/OpsForwarderAuditView.vue'),
    meta: { title: '货代审核', subject: 'OPERATOR' },
  },
  {
    /**
     * 🔴 平台运营 · 租户审核台（O3）—— 货代租户注册审核（doc/57 §C2 方案C）。
     *
     * <h2>⚠️ 与上面几屏同型：桌面管理后台的一屏，安全靠后端</h2>
     * 后端 {@code OpsTenantController}（{@code /ops/v1/tenants}）判 OPERATOR 主体 +
     * {@code ops:account:view} 权限点。这里的 {@code meta.subject='OPERATOR'} 只是
     * 「别让人走进一个必然 403 的界面」，⛔ 不是权限。
     *
     * <h2>🔴🔴 与货代资质审核（O2）是两道不同的闸</h2>
     * O2 改的是 {@code forwarder.status}（货代能不能被卖家绑定接单）；
     * 这屏改的是 {@code tenant.status}（这个租户身份能不能登录 / 用系统）。
     * 货代租户注册起步 {@code PENDING}，审核通过后 {@code ACTIVE} 才能用。
     */
    path: '/ops/tenants',
    name: 'ops-tenants',
    component: () => import('@/views/OpsTenantAuditView.vue'),
    meta: { title: '租户审核', subject: 'OPERATOR' },
  },
  {
    /** 消息中心 —— 后端 /api/v1/** 仅接受租户主体。 */
    path: '/notifications',
    name: 'notifications',
    component: () => import('@/views/NotificationView.vue'),
    meta: { title: '消息中心', subject: 'TENANT' },
  },
  /**
   * ── 货代管理后台（桌面后台，doc/59 §零）─────────────────────────
   *
   * <h2>🔴🔴 这一族与下面的 `/tasks` PWA 作业页是<b>两套范式</b>，但都属于货代（FORWARDER）</h2>
   * · `/fwd/settlement` / `/fwd/payee` —— 货代老板 / 财务用的<b>桌面后台</b>（左菜单 + 多页签），
   *   复用 seller 的 AppShell 外壳。调的是货代端点 {@code /fwd/v1/**}。
   * · `/tasks` 及其子路由 —— 仓库现场单手用的<b>PWA 作业页</b>（暗色、顶栏、底 tabbar），
   *   由 ForwarderFrame 自己提供外壳。
   * ⇒ 两者<b>共用 FORWARDER 令牌</b>，AppShell 按当前路由路径分流外壳（见 AppShell.vue 的 forwarderPwa）。
   *
   * <p>安全上与 OPERATOR 同理：后端 {@code SubjectTypeGuardFilter} 把 {@code /fwd/v1/**} 限定为
   * FORWARDER 令牌 ⇒ 卖家即使走到这一页，每一个请求都 403。
   * 🔴 下面 {@code meta.subject = 'FORWARDER'} 只是<b>不让人走进一个必然全是错误的界面</b>。
   */
  {
    path: '/fwd/settlement',
    name: 'fwd-settlement',
    component: () => import('@/views/FwdSettlementView.vue'),
    meta: { title: '我的结算单', subject: 'FORWARDER' },
  },
  {
    path: '/fwd/payee',
    name: 'fwd-payee',
    component: () => import('@/views/FwdPayeeView.vue'),
    meta: { title: '收款信息', subject: 'FORWARDER' },
  },
  {
    /**
     * 货代首次设备授权（桌面后台确认仓库设备请求）。
     *
     * 仓库设备展示二维码或 8 位确认码；已登录货代核对后明确确认。
     *
     * <p>安全上与 {@code /fwd/settlement} 同型：后端 {@code ForwarderLoginQrController} 自己校验
     * 主体类型（只 FORWARDER 能生成），{@code meta.subject='FORWARDER'} 只是
     * 「别让人走进一个必然 403 的界面」，⛔ 不是权限。
     */
    path: '/fwd/qr-login',
    name: 'fwd-qr-login',
    component: () => import('@/views/FwdQrLoginView.vue'),
    meta: { title: '首次设备授权', subject: 'FORWARDER' },
  },
  {
    /**
     * 货代设备管理（踢出登录态）。
     *
     * <h2>🔴🔴 这一屏是「货代老板在桌面后台应急踢出仓库设备」的功能</h2>
     * 场景：员工离职 / 手机丢了 / 换设备。点「全部踢出」⇒ 仓库设备上的 PWA 下一个请求就被拒
     * （实时，不等 15 分钟令牌过期）。后端 Redis 黑名单实现，{@code TenantContextFilter} 命中即 401。
     *
     * <p>安全上与 {@code /fwd/payee} 同型：后端 {@code ForwarderProfileController} 自己校验主体类型
     * （只 FORWARDER 能调 {@code /fwd/v1/**}），{@code meta.subject='FORWARDER'} 只是
     * 「别让人走进一个必然 403 的界面」，⛔ 不是权限。
     */
    path: '/fwd/sessions',
    name: 'fwd-sessions',
    component: () => import('@/views/FwdSessionsView.vue'),
    meta: { title: '设备管理', subject: 'FORWARDER' },
  },
  {
    /**
     * 平台运营 · 服务商密钥管理（短信 / 支付 / 快递）。
     *
     * <h2>🔴 与充值确认台 / 报表同型：安全靠后端</h2>
     * 后端三个 Ops Controller 判 OPERATOR 主体 + {@code ops:account:view} 权限点。
     * 这里的 {@code meta.subject='OPERATOR'} 只是「别让人走进必然 403 的界面」。
     *
     * <h2>⚠️ 平台级全局配置（无 tenant_id）</h2>
     * 改这里影响所有租户。密钥只能覆盖不能查看（安全纪律）。
     */
    path: '/ops/provider-keys',
    name: 'ops-provider-keys',
    component: () => import('@/views/OpsProviderKeysView.vue'),
    meta: { title: '服务商密钥', subject: 'OPERATOR' },
  },
  {
    /**
     * 🔴 平台运营 · 费率配置台（O4）—— doc/54 C1 写入口落地。
     *
     * <h2>⚠️ 与上面几屏同型：桌面管理后台的一屏，安全靠后端</h2>
     * 后端 {@code OpsFeeProfileController}（{@code /ops/v1/fee-profiles}）判 OPERATOR 主体 +
     * {@code ops:account:view} 权限点。这里的 {@code meta.subject='OPERATOR'} 只是
     * 「别让人走进一个必然 403 的界面」，⛔ 不是权限。
     *
     * <h2>🔴🔴 写入口是「新增版本行」语义，不是覆盖</h2>
     * 「同一 fee_key + 维度组合」已有 ACTIVE 行 ⇒ 后端归档旧行 + 插新行 version+1。
     * 运营在这屏配/改费率（commission / vat / label_fee / unit_service_fee 等），
     * 是定价模块的地基 —— 旧的 ACTIVE 行永远留着可追溯。
     */
    path: '/ops/fee-profiles',
    name: 'ops-fee-profiles',
    component: () => import('@/views/OpsFeeProfileView.vue'),
    meta: { title: '费率配置', subject: 'OPERATOR' },
  },
  {
    /**
     * 🔴 平台运营 · 国际物流分段报价台（P0-b 运费来源的维护入口，V46）。
     *
     * 保本价方程的国际运费一项来自 shipping_rate_rule 分段表——49 号拍板的
     * 三个候选来源（货代报价导入 / 平台接口 / 维护表）的共同落点。
     * 运营在这屏录平台默认段（tenantId=0）与租户议价段（tenantId=真实）。
     *
     * <h2>⚠️ 与费率配置台同型：安全靠后端</h2>
     * 后端 OpsShippingRateController（/ops/v1/shipping-rates）判 OPERATOR 主体 +
     * ops:account:view 权限点。这里的 meta.subject='OPERATOR' 只是
     * 「别让人走进一个必然 403 的界面」，⛔ 不是权限。
     */
    path: '/ops/shipping-rates',
    name: 'ops-shipping-rates',
    component: () => import('@/views/OpsShippingRateView.vue'),
    meta: { title: '运费预估参数', subject: 'OPERATOR' },
  },
  {
    /**
     * 🔴 平台运营 · 数据与防护配置台（一期补丁 A4，§9.3 闭环矩阵硬性项）。
     *
     * <h2>⚠️ 与费率配置台同型：安全靠后端</h2>
     * 后端 {@code OpsDataConfigController}（{@code /ops/v1/data-config}）判 OPERATOR 主体 +
     * {@code ops:account:view} 权限点。这里的 {@code meta.subject='OPERATOR'} 只是
     * 「别让人走进一个必然 403 的界面」，⛔ 不是权限。
     *
     * <h2>配置集中原则</h2>
     * 全部可配阈值收进 {@code data_governance_config} 一张表（tenantId=0 平台默认，
     * 可按租户覆盖；读取优先级：租户覆盖 &gt; 平台默认 &gt; 内置默认）。
     * 只允许写后端注册表（{@code DataGovernanceConfigKeys}）里的键。
     */
    path: '/ops/data-config',
    name: 'ops-data-config',
    component: () => import('@/views/OpsDataConfigView.vue'),
    meta: { title: '数据与防护配置', subject: 'OPERATOR' },
  },
  {
    path: '/ops/register-seller',
    name: 'ops-register-seller',
    component: () => import('@/views/OpsSellerRegistrationView.vue'),
    meta: { title: '人工开通卖家', subject: 'OPERATOR' },
  },
  {
    path: '/ops/ai-config',
    name: 'ops-ai-config',
    component: () => import('@/views/OpsAiConfigView.vue'),
    meta: { title: 'AI 模型配置', subject: 'OPERATOR' },
  },
  {
    path: '/ops/price-plans',
    name: 'ops-price-plans',
    component: () => import('@/views/OpsBillingView.vue'),
    props: { section: 'plans' },
    meta: { title: '价格方案', subject: 'OPERATOR' },
  },
  {
    path: '/ops/subscriptions',
    name: 'ops-subscriptions',
    component: () => import('@/views/OpsBillingView.vue'),
    props: { section: 'subscriptions' },
    meta: { title: '订阅管理', subject: 'OPERATOR' },
  },
  {
    path: '/ops/entitlements',
    name: 'ops-entitlements',
    component: () => import('@/views/OpsBillingView.vue'),
    props: { section: 'entitlements' },
    meta: { title: '特殊权益与审计', subject: 'OPERATOR' },
  },
  {
    path: '/ops/unit-economics',
    name: 'ops-unit-economics',
    component: () => import('@/views/OpsBillingView.vue'),
    props: { section: 'economics' },
    meta: { title: '单位经济', subject: 'OPERATOR' },
  },
  {
    path: '/ops/commercial-extended',
    name: 'ops-commercial-extended',
    component: () => import('@/views/OpsCommercialExtendedView.vue'),
    meta: { title: 'Phase2 商业化', subject: 'OPERATOR' },
  },
  /**
   * ── 货代端路由（2026-08-06 合并：登录选「货代」角色后进入）──────────────
   *
   * <h2>🔴🔴 为什么货代端**并了进来**，而早先注释里说它「必须分出去」</h2>
   * 旧的设计判断（见上面 OPERATOR 那一段）是：货代端是 PWA（仓库设备、离线、单手、
   * Capacitor 原生壳），与桌面管理后台交互形态相反 ⇒ 分成两个工程。
   * 现在的真相是：**三端共用一个登录入口**，登录后再分流。让库管在登录页选完「货代」
   * 却被 `window.location` 跳到另一个端口，开发期还会因为跨源交接失败（见 tokens.ts
   * 的 `handOffToForwarder`）进不去 —— 这一跳是纯负担。
   * ⇒ 货代页面并进 seller，登录后按 `subjectType` 路由，与 OPERATOR 同范式。
   *
   * <p>⚠️ 但货代那一套**仓库作业主题**（暗色、56px 触摸按钮、底部 tabbar）仍然要保留 ——
   * 它是站在仓库里单手用的，不是坐姿后台。⇒ 这些路由挂在一个 {@link ForwarderFrame}
   * 下，由它套 `.fwd-scope` 把暗色主题**收在货代这一片**，⛔ 不渗到卖家端外壳。
   *
   * <p>⚠️ 货代页面与 api 文件**原样复制自 forwarder 工程**（`@/forwarder/...`），
   * forwarder 工程本身**保留**（PWA / Capacitor 打包仍需要它），见任务说明的「不做的」。
   *
   * <p>🔴 `/settlement` 改名 `/fwd-settlement`：避免和卖家端将来可能加的 `/settlement` 撞。
   *
   * <p>安全上与 OPERATOR 同理：后端 `SubjectTypeGuardFilter` 把 `/fwd/v1/**` 限定为
   * FORWARDER 令牌 ⇒ 卖家即使走到这一页，每一个请求都 403。
   * 🔴 下面 `meta.subject = 'FORWARDER'` 只是**不让人走进一个必然全是错误的界面**。
   */
  {
    path: '/tasks',
    component: () => import('@/forwarder/ForwarderFrame.vue'),
    meta: { subject: 'FORWARDER' },
    children: [
      {
        path: '',
        name: 'tasks',
        component: () => import('@/forwarder/views/TaskEntryView.vue'),
        meta: { title: '任务入口', subject: 'FORWARDER' },
      },
      {
        path: '/work/:orderId',
        name: 'work',
        component: () => import('@/forwarder/views/WorkbenchView.vue'),
        meta: { title: '作业', subject: 'FORWARDER' },
        props: true,
      },
      {
        path: '/drafts',
        name: 'drafts',
        component: () => import('@/forwarder/views/DraftsView.vue'),
        meta: { title: '草稿箱', subject: 'FORWARDER' },
      },
      {
        path: '/fwd-settlement',
        name: 'fwd-settlement-pwa',
        component: () => import('@/forwarder/views/SettlementView.vue'),
        meta: { title: '结算', subject: 'FORWARDER' },
      },
      {
        path: '/gaps',
        name: 'gaps',
        // 保留旧书签入口，作业与离线草稿仍使用现有货代工作区。
        redirect: '/tasks',
      },
    ],
  },
  // 未知地址与根入口使用同一主体分流；匿名会话不得先进入受保护页面。
  {
    path: '/:pathMatch(.*)*',
    redirect: () => rootEntryForSubject(currentSession()?.subjectType),
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
})

router.afterEach((to) => {
  const title = typeof to.meta.title === 'string' ? to.meta.title : ''
  // 🔴 品牌名不带平台名 —— 理由见 LoginView.vue
  document.title = title ? `${title} · 简界` : '简界'
})

/** 所有构建环境均要求真实会话；前端守卫负责导航，后端仍独立校验主体和权限。 */
router.beforeEach(async (to) => {
  const s = currentSession()
  if (to.name === 'change-password') return s ? true : { name: 'login' }
  if (to.meta.layout === 'blank') return true
  if (s?.mustChangePassword) return { name: 'change-password' }

  /**
   * 🔴 主体隔离：运营的页面不给卖家看，反之亦然。
   *
   * ⚠️ 它**不是权限** —— 真正的权限在后端（`SubjectTypeGuardFilter` 按路径前缀
   * 把 `/ops/v1/**` 限定为 OPERATOR）。这一层只负责**不让人走进一个必然全是错误的界面**。
   * ⛔ 绝不能因为"前端已经拦了"就去掉后端那一层。
   *
   * 🔴 这一段与登录检查在开发构建里也照常生效：
   * 它挡的不是"没登录"，而是"登错了角色"，而那在开发期同样会造成误判。
   */
  // 未声明主体的受保护路由统一属于 TENANT；不要再靠“不是运营/货代”反推。
  const need = (to.meta.subject as string | undefined) ?? 'TENANT'
  if (need && s && s.subjectType !== need) {
    // 🔴 按当前主体把人送回他自己那一摊，而不是甩到登录页
    if (s.subjectType === 'OPERATOR') return { path: '/ops/recharges' }
    // 🔴 货代首页 = 我的结算单（桌面后台，doc/59 §零），不再是 PWA 作业入口 /tasks
    if (s.subjectType === 'FORWARDER') return { path: '/fwd/settlement' }
    return { path: '/workbench' }
  }
  if (!s) return { name: 'login', query: { redirect: to.fullPath } }
  const feature = to.meta.phase2Feature as Phase2Feature | undefined
  if (feature) {
    let unavailable = false
    const state = await loadPhase2Features(s.subjectType, () => { unavailable = true })
    if (unavailable) return { path: rootEntryForSubject(s.subjectType), query: { featureRetry: to.fullPath } }
    if (!state[feature]) return { path: rootEntryForSubject(s.subjectType) }
  }
  return true
})

export default router
